import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiReconstructedDocumentDetail } from '@avisoma/shared';
// Value-imported from the compiled file, not the `@avisoma/shared` barrel: the
// barrel re-exports `RequestSignature` (Node `crypto`/`http`) into the Vite bundle.
import { EXTRACT_AS_CATEGORIES } from '@avisoma/shared/dist/api-schema/paths/extract-as-categories.js';
import { Button } from '../../catalyst/button';
import { Dialog, DialogTitle, DialogDescription, DialogActions } from '../../catalyst/dialog';
import backend from '../../services/backend';

const EXTRACTOR_CATEGORIES = new Set<string>(EXTRACT_AS_CATEGORIES);

export function OverridesCard({
  entity,
  reextracting,
  reextractCount,
  onReextract,
}: {
  entity: ApiReconstructedDocumentDetail;
  reextracting: boolean;
  reextractCount?: { enqueued: number; total: number } | null;
  onReextract: () => void;
}) {
  const queryClient = useQueryClient();
  // Local state seeds at first mount. We deliberately don't sync from `entity`
  // on EVERY prop change — doing so would clobber an in-progress edit when a
  // PATCH refetch lands (see c775fd734). But we DO need to reset when the
  // entity identity changes (e.g. the user navigates to a different
  // /reconstructed-documents/:id), since react-router reuses the same component
  // instance across param-only route changes.
  const [localUserSuggested, setLocalUserSuggested] = useState(entity.userSuggestedCategory ?? '');
  const [localTags, setLocalTags] = useState<string[]>(entity.tags);
  const [newTag, setNewTag] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setLocalUserSuggested(entity.userSuggestedCategory ?? '');
    setLocalTags(entity.tags);
    setNewTag('');
    setConfirmOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only on entity-id change
  }, [entity.id]);

  // Mirror server-side computeEffectiveCategory but use the local intent so the
  // button label matches the dropdown the user just touched, not the stale
  // entity prop (which only updates after the PATCH refetch lands).
  const trimmedLocalOverride = localUserSuggested.trim();
  const effectiveCategory = trimmedLocalOverride || entity.classification;
  const effectiveIsExtractor = EXTRACTOR_CATEGORIES.has(effectiveCategory);

  const categoriesQuery = useQuery({
    queryKey: ['distinct-categories'],
    queryFn: async () => {
      const res = await backend.getPagesDistinctCategories();
      return res.data.items ?? [];
    },
  });

  type PatchVariables = {
    id: string;
    data: { userSuggestedCategory?: string | null; tags?: string[] };
  };

  const patch = useMutation({
    mutationFn: async ({ id, data }: PatchVariables) => {
      const res = await backend.patchReconstructedDocument(id, data);
      return res.data.item;
    },
    onError: (error, variables) => {
      // Only revert if the entity in the closure (the one currently mounted) is
      // the same one whose PATCH just failed. If the user navigated mid-flight,
      // entity refers to a DIFFERENT document and we must not clobber its state.
      if (variables.id !== entity.id) return;
      // 409: the category row was saved and the enqueue failed. Keep the
      // selection. Reverting it shows a category the server no longer has.
      const status = (error as { response?: { status?: number } }).response?.status;
      const categorySaved = status === 409 && variables.data.userSuggestedCategory !== undefined;
      if (!categorySaved) {
        setLocalUserSuggested(entity.userSuggestedCategory ?? '');
      }
      setLocalTags(entity.tags);
    },
    onSettled: (_item, _error, variables) => {
      // Invalidate the entity the PATCH actually targeted, not whatever's mounted
      // now. If the user navigated mid-flight, this refreshes the cache for the
      // original entity so a back-navigation shows up-to-date data.
      // Refetch instead of optimistic setQueryData: out-of-order responses
      // can't corrupt the cache because the refetch reads whatever the server
      // actually committed last in request-arrival order.
      queryClient.invalidateQueries({ queryKey: ['reconstructed-document', variables.id] });
    },
  });

  const handleSaveCategory = (value: string) => {
    const next = value === '' ? null : value;
    if (next === entity.userSuggestedCategory) return;
    patch.mutate({ id: entity.id, data: { userSuggestedCategory: next } });
  };

  const handleAddTag = () => {
    const t = newTag.trim();
    if (!t || localTags.includes(t)) {
      setNewTag('');
      return;
    }
    const next = [...localTags, t];
    setLocalTags(next);
    setNewTag('');
    patch.mutate({ id: entity.id, data: { tags: next } });
  };

  const handleRemoveTag = (t: string) => {
    const next = localTags.filter((x) => x !== t);
    setLocalTags(next);
    patch.mutate({ id: entity.id, data: { tags: next } });
  };

  return (
    <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 shadow sm:rounded-lg p-4 sm:p-5">
      <h2 className="text-sm font-medium text-gray-700 dark:text-zinc-200">Overrides</h2>

      <div className="mt-3 grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5 text-sm items-center">
        <div className="text-gray-500 dark:text-zinc-400">Algorithm</div>
        <div className="text-gray-900 dark:text-zinc-100">
          {entity.classification}
          <span className="ml-1 text-xs text-gray-500 dark:text-zinc-500">
            ({Math.round(entity.classificationCertainty * 100)}%)
          </span>
        </div>

        <label htmlFor="user-suggested" className="text-gray-500 dark:text-zinc-400">
          User-suggested
        </label>
        <div className="flex flex-col gap-1">
          <select
            id="user-suggested"
            value={localUserSuggested}
            onChange={(e) => {
              setLocalUserSuggested(e.target.value);
              handleSaveCategory(e.target.value);
            }}
            className="w-full min-w-0 bg-white dark:bg-zinc-900 border border-gray-300 dark:border-zinc-700 rounded-md px-2 py-1 text-sm text-gray-900 dark:text-zinc-100"
            disabled={patch.isPending}
          >
            <option value="">— use algorithm classification —</option>
            {(categoriesQuery.data ?? []).map((c: string) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {!effectiveIsExtractor && (
            <span className="text-xs text-amber-700 dark:text-amber-400">
              "{effectiveCategory}" is not a re-extractable category. Re-extract is disabled.
            </span>
          )}
        </div>

        <div className="text-gray-500 dark:text-zinc-400">Tags</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {localTags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded whitespace-nowrap bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 text-xs"
            >
              {t}
              <button
                type="button"
                onClick={() => handleRemoveTag(t)}
                className="opacity-60 hover:opacity-100 leading-none"
                aria-label={`Remove tag ${t}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
            onBlur={handleAddTag}
            placeholder="Add tag…"
            className="bg-transparent border-b border-gray-300 dark:border-zinc-700 px-1 py-0.5 text-xs text-gray-900 dark:text-zinc-100 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400"
          />
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-zinc-700 flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500 dark:text-zinc-500">
          {reextracting
            ? reextractCount
              ? `Re-extracting ${reextractCount.enqueued} of ${reextractCount.total} page${reextractCount.total === 1 ? '' : 's'}…`
              : 'Re-extracting…'
            : null}
        </div>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={reextracting || patch.isPending || !effectiveIsExtractor}
          title={
            patch.isPending
              ? 'Saving category change…'
              : !effectiveIsExtractor
                ? `"${effectiveCategory}" is not a re-extractable category`
                : undefined
          }
          className="inline-flex items-center px-3 py-1.5 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Re-extract all pages as {effectiveCategory} (agentic)
        </button>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Re-extract all pages?</DialogTitle>
        <DialogDescription>
          This re-runs extraction on all {entity.pages.length} pages with category <strong>{effectiveCategory}</strong> and
          overwrites each page's existing <code>dataExtraction.result</code>. Existing per-page results are not preserved.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={() => {
              setConfirmOpen(false);
              onReextract();
            }}
            disabled={patch.isPending || !effectiveIsExtractor}
          >
            {patch.isPending ? 'Saving…' : 'Re-extract'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
